<template>
    <div class="w-full">
        <!-- Header avec recherche et bouton d'ajout -->
        <BaseCard padding="md" class="mb-5">
            <div class="flex flex-wrap gap-4">
                <div class="flex-1 min-w-[300px]">
                    <BaseInput
                        v-model="search"
                        type="search"
                        placeholder="Rechercher une catégorie..."
                        @input="onSearchInput"
                        clearable
                    >
                        <template #icon-left>
                            <BaseIcon name="search" size="18" color="#9ca3af" />
                        </template>
                    </BaseInput>
                </div>
                <BaseButton variant="primary" @click="$emit('add')">
                    <template #icon-left>
                        <BaseIcon name="plus" size="16" color="white" />
                    </template>
                    Ajouter une catégorie
                </BaseButton>
            </div>
        </BaseCard>

        <!-- Toggle de vue (liste/arbre) -->
        <div class="flex bg-white rounded-lg p-1 w-fit mb-5 shadow-sm">
            <button
                :class="[
                    'flex items-center gap-2 px-4 py-2 rounded-md text-sm transition-all',
                    viewMode === 'list' ? 'bg-gray-100 text-primary font-medium' : 'text-gray-600 hover:text-primary'
                ]"
                @click="viewMode = 'list'"
            >
                <BaseIcon name="list" size="18" />
                Liste
            </button>
            <button
                :class="[
                    'flex items-center gap-2 px-4 py-2 rounded-md text-sm transition-all',
                    viewMode === 'tree' ? 'bg-gray-100 text-primary font-medium' : 'text-gray-600 hover:text-primary'
                ]"
                @click="viewMode = 'tree'"
            >
                <BaseIcon name="folder" size="18" />
                Arborescence
            </button>
        </div>

        <!-- Loader -->
        <BaseCard v-if="loading" padding="xl" class="text-center">
            <div class="flex flex-col items-center justify-center py-12">
                <div class="w-10 h-10 border-3 border-gray-200 border-t-primary rounded-full animate-spin mb-4"></div>
                <p class="text-gray-600">Chargement des catégories...</p>
            </div>
        </BaseCard>

        <!-- Message si aucune catégorie -->
        <BaseCard v-else-if="!loading && filteredCategories.length === 0" padding="xl" class="text-center">
            <div class="flex flex-col items-center justify-center py-12">
                <div class="w-16 h-16 mb-4 text-gray-400">
                    <BaseIcon name="folder" size="64" color="#94a3b8" />
                </div>
                <h3 class="text-gray-800 text-lg mb-2">Aucune catégorie trouvée</h3>
                <p class="text-gray-600 max-w-md">Créez votre première catégorie en cliquant sur le bouton "Ajouter une catégorie".</p>
            </div>
        </BaseCard>

        <!-- Liste de catégories -->
        <div v-else-if="viewMode === 'list'" class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5 mb-8">
            <BaseCard
                v-for="category in filteredCategories"
                :key="category.id"
                padding="lg"
                class="flex flex-col hover:-translate-y-1 hover:shadow-xl transition-all border border-gray-100"
            >
                <h3 class="text-lg font-semibold text-gray-800 mb-2.5 leading-tight">{{ category.name }}</h3>

                <p v-if="category.description" class="text-gray-600 text-sm mb-4 leading-relaxed flex-grow">
                    {{ truncateText(category.description, 100) }}
                </p>

                <div class="flex flex-wrap gap-3 mb-4 text-xs">
                    <BaseBadge v-if="category.products_count" variant="success" class="px-2 py-1">
                        {{ category.products_count }} produit{{ category.products_count > 1 ? 's' : '' }}
                    </BaseBadge>
                    <BaseBadge v-if="getParentName(category.parent_category_id)" variant="secondary" class="px-2 py-1">
                        Parent: {{ getParentName(category.parent_category_id) }}
                    </BaseBadge>
                </div>

                <div class="flex gap-2.5 mt-auto">
                    <BaseButton variant="outline" size="sm" class="flex-1" @click="$emit('edit', category)">
                        <template #icon-left>
                            <BaseIcon name="edit" size="16" />
                        </template>
                        Modifier
                    </BaseButton>
                    <BaseButton variant="danger-outline" size="sm" class="flex-1" @click="$emit('delete', category)">
                        <template #icon-left>
                            <BaseIcon name="delete" size="16" />
                        </template>
                        Supprimer
                    </BaseButton>
                </div>
            </BaseCard>
        </div>

        <!-- Vue arborescente des catégories -->
        <BaseCard v-else padding="lg" class="mb-8">
            <div class="w-full">
                <div class="tree-node root-node">
                    <div class="font-semibold text-gray-700 text-base mb-3">Toutes les catégories</div>
                    <div class="tree-children">
                        <template v-for="category in rootCategories" :key="category.id">
                            <div class="tree-node mb-1">
                                <div
                                    class="flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer transition-colors hover:bg-gray-50 group"
                                    @click="toggleNode(category.id)"
                                >
                                    <span
                                        v-if="hasChildren(category.id)"
                                        class="flex items-center justify-center w-5 h-5 text-gray-600 transition-transform"
                                        :class="{ 'rotate-90': expandedNodes.includes(category.id) }"
                                    >
                                        <BaseIcon name="chevron-right" size="16" />
                                    </span>
                                    <span class="flex-grow text-sm text-gray-800">{{ category.name }}</span>
                                    <div class="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                        <button
                                            class="flex items-center justify-center w-7 h-7 bg-blue-50 text-primary rounded hover:bg-blue-100 transition-colors"
                                            @click.stop="$emit('edit', category)"
                                        >
                                            <BaseIcon name="edit" size="14" />
                                        </button>
                                        <button
                                            class="flex items-center justify-center w-7 h-7 bg-red-50 text-danger rounded hover:bg-red-100 transition-colors"
                                            @click.stop="$emit('delete', category)"
                                        >
                                            <BaseIcon name="delete" size="14" />
                                        </button>
                                    </div>
                                </div>
                                <div v-if="expandedNodes.includes(category.id)" class="pl-6 mt-1">
                                    <template v-for="childCategory in getChildrenCategories(category.id)" :key="childCategory.id">
                                        <CategoryTreeNode
                                            :category="childCategory"
                                            :all-categories="categories"
                                            :expanded-nodes="expandedNodes"
                                            @toggle-node="toggleNode"
                                            @edit="$emit('edit', $event)"
                                            @delete="$emit('delete', $event)"
                                        />
                                    </template>
                                </div>
                            </div>
                        </template>
                    </div>
                </div>
            </div>
        </BaseCard>

        <!-- Pagination -->
        <BaseCard v-if="viewMode === 'list' && !loading && pagination.total > pagination.perPage" padding="md">
            <div class="flex justify-between items-center flex-wrap gap-4">
                <div class="text-sm text-gray-600">
                    Affichage de {{ (pagination.currentPage - 1) * pagination.perPage + 1 }} à
                    {{ Math.min(pagination.currentPage * pagination.perPage, pagination.total) }}
                    sur {{ pagination.total }} catégories
                </div>
                <div class="flex items-center gap-3">
                    <BaseButton
                        variant="outline"
                        size="sm"
                        :disabled="pagination.currentPage === 1"
                        @click="$emit('page-change', pagination.currentPage - 1)"
                    >
                        <template #icon-left>
                            <BaseIcon name="chevron-left" size="16" />
                        </template>
                        Précédent
                    </BaseButton>

                    <div class="flex gap-1">
                        <button
                            v-for="page in displayedPages"
                            :key="page"
                            :class="[
                                'flex items-center justify-center w-9 h-9 rounded-md text-sm font-medium transition-all',
                                page === pagination.currentPage
                                    ? 'bg-primary text-white'
                                    : 'bg-white text-gray-800 border border-gray-300 hover:bg-gray-50'
                            ]"
                            @click="$emit('page-change', page)"
                        >
                            {{ page }}
                        </button>
                    </div>

                    <BaseButton
                        variant="outline"
                        size="sm"
                        :disabled="pagination.currentPage >= pagination.lastPage"
                        @click="$emit('page-change', pagination.currentPage + 1)"
                    >
                        Suivant
                        <template #icon-right>
                            <BaseIcon name="chevron-right" size="16" />
                        </template>
                    </BaseButton>
                </div>
            </div>
        </BaseCard>
    </div>
</template>

<script>
// Composant récursif pour l'affichage de l'arbre des catégories
const CategoryTreeNode = {
    name: 'CategoryTreeNode',
    props: {
        category: {
            type: Object,
            required: true
        },
        allCategories: {
            type: Array,
            required: true
        },
        expandedNodes: {
            type: Array,
            default: () => []
        }
    },
    methods: {
        hasChildren(categoryId) {
            return this.allCategories.some(cat => cat.parent_category_id === categoryId);
        },
        getChildrenCategories(parentId) {
            return this.allCategories.filter(cat => cat.parent_category_id === parentId);
        },
        toggleNode(categoryId) {
            this.$emit('toggle-node', categoryId);
        }
    },
    template: `
      <div class="tree-node mb-1">
        <div
          class="flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer transition-colors hover:bg-gray-50 group"
          @click="toggleNode(category.id)"
        >
          <span
            v-if="hasChildren(category.id)"
            class="flex items-center justify-center w-5 h-5 text-gray-600 transition-transform"
            :class="{ 'rotate-90': expandedNodes.includes(category.id) }"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <polyline points="9 18 15 12 9 6"></polyline>
            </svg>
          </span>
          <span class="flex-grow text-sm text-gray-800">{{ category.name }}</span>
          <div class="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
            <button
              class="flex items-center justify-center w-7 h-7 bg-blue-50 text-primary rounded hover:bg-blue-100 transition-colors"
              @click.stop="$emit('edit', category)"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"></path>
              </svg>
            </button>
            <button
              class="flex items-center justify-center w-7 h-7 bg-red-50 text-danger rounded hover:bg-red-100 transition-colors"
              @click.stop="$emit('delete', category)"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <polyline points="3 6 5 6 21 6"></polyline>
                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                <line x1="10" y1="11" x2="10" y2="17"></line>
                <line x1="14" y1="11" x2="14" y2="17"></line>
              </svg>
            </button>
          </div>
        </div>
        <div v-if="expandedNodes.includes(category.id)" class="pl-6 mt-1">
          <template v-for="childCategory in getChildrenCategories(category.id)" :key="childCategory.id">
            <CategoryTreeNode
              :category="childCategory"
              :all-categories="allCategories"
              :expanded-nodes="expandedNodes"
              @toggle-node="toggleNode"
              @edit="$emit('edit', $event)"
              @delete="$emit('delete', $event)"
            />
          </template>
        </div>
      </div>
    `
};

export default {
    name: 'CategoriesList',
    components: {
        CategoryTreeNode
    },
    props: {
        categories: {
            type: Array,
            default: () => []
        },
        loading: {
            type: Boolean,
            default: false
        },
        pagination: {
            type: Object,
            default: () => ({
                currentPage: 1,
                perPage: 10,
                total: 0,
                lastPage: 1
            })
        }
    },
    data() {
        return {
            search: '',
            viewMode: 'list',
            expandedNodes: [],
            searchTimeout: null
        }
    },
    computed: {
        filteredCategories() {
            if (!this.search) return this.categories;

            const searchTerm = this.search.toLowerCase();
            return this.categories.filter(category =>
                category.name.toLowerCase().includes(searchTerm) ||
                (category.description && category.description.toLowerCase().includes(searchTerm))
            );
        },
        rootCategories() {
            return this.categories.filter(cat => !cat.parent_category_id);
        },
        displayedPages() {
            const pages = [];
            const lastPage = this.pagination.lastPage || 1;
            const currentPage = this.pagination.currentPage;

            // Afficher au maximum 5 pages
            if (lastPage <= 5) {
                // Moins de 5 pages, afficher toutes les pages
                for (let i = 1; i <= lastPage; i++) {
                    pages.push(i);
                }
            } else {
                // Plus de 5 pages, afficher les pages intelligemment
                if (currentPage <= 3) {
                    // Près du début
                    for (let i = 1; i <= 5; i++) {
                        pages.push(i);
                    }
                } else if (currentPage >= lastPage - 2) {
                    // Près de la fin
                    for (let i = lastPage - 4; i <= lastPage; i++) {
                        pages.push(i);
                    }
                } else {
                    // Au milieu
                    for (let i = currentPage - 2; i <= currentPage + 2; i++) {
                        pages.push(i);
                    }
                }
            }

            return pages;
        }
    },
    methods: {
        onSearchInput() {
            clearTimeout(this.searchTimeout);
            this.searchTimeout = setTimeout(() => {
                if (this.viewMode === 'list') {
                    this.$emit('search', this.search);
                }
            }, 500);
        },
        truncateText(text, length) {
            if (!text) return '';
            return text.length > length ? text.substring(0, length) + '...' : text;
        },
        getParentName(parentId) {
            if (!parentId) return '';
            const parent = this.categories.find(cat => cat.id === parentId);
            return parent ? parent.name : '';
        },
        hasChildren(categoryId) {
            return this.categories.some(cat => cat.parent_category_id === categoryId);
        },
        getChildrenCategories(parentId) {
            return this.categories.filter(cat => cat.parent_category_id === parentId);
        },
        toggleNode(categoryId) {
            const index = this.expandedNodes.indexOf(categoryId);
            if (index === -1) {
                this.expandedNodes.push(categoryId);
            } else {
                this.expandedNodes.splice(index, 1);
            }
        }
    }
}
</script>

<style scoped>
/* Minimal scoped styles - most styling is done with Tailwind */
</style>
